import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AdBanner from '../components/AdBanner';
import SEO from '../components/SEO';
import './PasswordGenerator.css';

const characterSets = {
  uppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
  lowercase: 'abcdefghijklmnopqrstuvwxyz',
  numbers: '0123456789',
  symbols: '!@#$%^&*()_+-=[]{}|;:,.<>?',
  similar: 'il1Lo0O',
  ambiguous: '{}[]()/\\\'"`~,;.<>'
};

function PasswordGenerator() {
  const [password, setPassword] = useState('');
  const [options, setOptions] = useState({
    length: 12,
    includeUppercase: true,
    includeLowercase: true,
    includeNumbers: true,
    includeSymbols: false,
    excludeSimilar: false,
    excludeAmbiguous: false
  });
  const [strength, setStrength] = useState({ score: 0, text: 'Weak', color: '#ea4335' });
  const [history, setHistory] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [customCharacters, setCustomCharacters] = useState('');
  const [excludeCharacters, setExcludeCharacters] = useState('');

  const presets = [
    { name: 'Easy to Say', length: 8, includeUppercase: true, includeLowercase: true, includeNumbers: false, includeSymbols: false, excludeSimilar: true, excludeAmbiguous: true },
    { name: 'Easy to Read', length: 12, includeUppercase: true, includeLowercase: true, includeNumbers: true, includeSymbols: false, excludeSimilar: true, excludeAmbiguous: false },
    { name: 'All Characters', length: 12, includeUppercase: true, includeLowercase: true, includeNumbers: true, includeSymbols: true, excludeSimilar: false, excludeAmbiguous: false },
    { name: 'High Security', length: 20, includeUppercase: true, includeLowercase: true, includeNumbers: true, includeSymbols: true, excludeSimilar: false, excludeAmbiguous: false }
  ];

  const replaceRandomChar = (password, newChar) => {
    const index = Math.floor(Math.random() * password.length);
    return password.substring(0, index) + newChar + password.substring(index + 1);
  };

  const checkPasswordStrength = (pwd) => {
    let score = 0;
    const checks = {
      length: pwd.length >= 8,
      longLength: pwd.length >= 12,
      veryLongLength: pwd.length >= 16,
      uppercase: /[A-Z]/.test(pwd),
      lowercase: /[a-z]/.test(pwd),
      numbers: /[0-9]/.test(pwd),
      symbols: /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(pwd),
      noRepeat: !/(.)\1{2,}/.test(pwd),
      noSequence: !/(?:abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz|123|234|345|456|567|678|789|890)/i.test(pwd)
    };

    // Calculate score
    if (checks.length) score += 1;
    if (checks.longLength) score += 1;
    if (checks.veryLongLength) score += 1;
    if (checks.uppercase) score += 1;
    if (checks.lowercase) score += 1;
    if (checks.numbers) score += 1;
    if (checks.symbols) score += 2;
    if (checks.noRepeat) score += 1;
    if (checks.noSequence) score += 1;

    let strengthText, strengthColor;
    if (score <= 3) {
      strengthText = 'Weak';
      strengthColor = '#ea4335';
    } else if (score <= 6) {
      strengthText = 'Fair';
      strengthColor = '#f9ab00';
    } else if (score <= 8) {
      strengthText = 'Good';
      strengthColor = '#34a853';
    } else {
      strengthText = 'Strong';
      strengthColor = '#137333';
    }

    setStrength({ score: (score / 10) * 100, text: strengthText, color: strengthColor });
  };

  const generatePassword = useCallback(() => {
    let charset = '';
    if (options.includeUppercase) charset += characterSets.uppercase;
    if (options.includeLowercase) charset += characterSets.lowercase;
    if (options.includeNumbers) charset += characterSets.numbers;
    if (options.includeSymbols) charset += characterSets.symbols;
    if (customCharacters) charset += customCharacters;
    let excludeSet = excludeCharacters;
    if (options.excludeSimilar) excludeSet += characterSets.similar;
    if (options.excludeAmbiguous) excludeSet += characterSets.ambiguous;
    if (excludeSet) {
      charset = charset.split('').filter(char => !excludeSet.includes(char)).join('');
    }
    charset = [...new Set(charset)].join('');
    if (charset.length === 0) {
      setPassword('');
      return;
    }
    let newPassword = '';
    for (let i = 0; i < options.length; i++) {
      newPassword += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    if (options.includeUppercase && !/[A-Z]/.test(newPassword) && characterSets.uppercase.split('').some(char => charset.includes(char))) {
      const upperChars = characterSets.uppercase.split('').filter(char => charset.includes(char));
      newPassword = replaceRandomChar(newPassword, upperChars[Math.floor(Math.random() * upperChars.length)]);
    }
    if (options.includeLowercase && !/[a-z]/.test(newPassword) && characterSets.lowercase.split('').some(char => charset.includes(char))) {
      const lowerChars = characterSets.lowercase.split('').filter(char => charset.includes(char));
      newPassword = replaceRandomChar(newPassword, lowerChars[Math.floor(Math.random() * lowerChars.length)]);
    }
    if (options.includeNumbers && !/[0-9]/.test(newPassword) && characterSets.numbers.split('').some(char => charset.includes(char))) {
      const numberChars = characterSets.numbers.split('').filter(char => charset.includes(char));
      newPassword = replaceRandomChar(newPassword, numberChars[Math.floor(Math.random() * numberChars.length)]);
    }
    if (options.includeSymbols && !/[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(newPassword) && characterSets.symbols.split('').some(char => charset.includes(char))) {
      const symbolChars = characterSets.symbols.split('').filter(char => charset.includes(char));
      newPassword = replaceRandomChar(newPassword, symbolChars[Math.floor(Math.random() * symbolChars.length)]);
    }
    setPassword(newPassword);
    checkPasswordStrength(newPassword);
  }, [options, customCharacters, excludeCharacters]);

  // Generate password when options change
  useEffect(() => {
    generatePassword();
  }, [generatePassword]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(password);
      
      // Add to history
      if (password) {
        const historyEntry = {
          password: password,
          strength: strength.text,
          timestamp: new Date().toLocaleTimeString(),
          options: { ...options }
        };
        setHistory(prev => [historyEntry, ...prev.slice(0, 9)]);
      }
    } catch (err) {
      console.error('Failed to copy password:', err);
    }
  };

  const applyPreset = (preset) => {
    setOptions({
      ...preset,
      excludeSimilar: preset.excludeSimilar || false,
      excludeAmbiguous: preset.excludeAmbiguous || false
    });
  };

  const updateOption = (key, value) => {
    setOptions(prev => ({ ...prev, [key]: value }));
  };

  const clearHistory = () => {
    setHistory([]);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Password Generator - Secure Password Creator",
    "description": "Generate strong, secure passwords with customizable criteria. Include uppercase, lowercase, numbers, and symbols. Check password strength and copy to clipboard.",
    "url": "https://infi-tools.com/password-generator",
    "applicationCategory": "SecurityApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": [
      "Generate secure passwords",
      "Customizable length (4-128 characters)",
      "Include/exclude character types",
      "Password strength indicator",
      "Copy to clipboard",
      "No data stored or transmitted",
      "Client-side generation for security"
    ]
  };

  return (
    <div className="password-generator-page">
      <SEO 
        title="Password Generator - Create Strong, Secure Passwords"
        description="Generate strong, secure passwords with customizable criteria. Include uppercase, lowercase, numbers, and symbols. Free password generator with strength checker."
        keywords="password generator, secure password, strong password, random password, password creator, password maker, secure password generator, password tool"
        canonicalUrl="https://infi-tools.com/password-generator"
        structuredData={structuredData}
      />
      <div className="container">
        <div className="generator-header">
          <Link to="/" className="back-link">← Back to Tools</Link>
          <h1>Password Generator</h1>
        </div>

        {/* Top Ad Banner */}
        <AdBanner adSlot="4351100473" style={{ marginBottom: '20px' }} />

        <div className="generator-container">
          {/* Generated Password Section */}
          <div className="password-section">
            <div className="password-display">
              <h2>Generated Password</h2>
              <div className="password-output">
                <input
                  type="text"
                  value={password}
                  readOnly
                  className="password-field"
                  placeholder="Generated password will appear here"
                />
                <button 
                  className="copy-btn"
                  onClick={copyToClipboard}
                  disabled={!password}
                  title="Copy to clipboard"
                >
                  📋
                </button>
              </div>
              
              {/* Password Strength */}
              <div className="strength-indicator">
                <div className="strength-label">Password Strength: <span style={{ color: strength.color }}>{strength.text}</span></div>
                <div className="strength-bar">
                  <div 
                    className="strength-fill"
                    style={{ 
                      width: `${strength.score}%`, 
                      backgroundColor: strength.color 
                    }}
                  ></div>
                </div>
              </div>
              
              <div className="password-actions">
                <button className="generate-btn" onClick={generatePassword}>
                  🔄 Generate New
                </button>
                <button 
                  className="copy-text-btn"
                  onClick={copyToClipboard}
                  disabled={!password}
                >
                  📋 Copy Password
                </button>
              </div>
            </div>

            {/* Presets */}
            <div className="presets-section">
              <h3>Quick Presets</h3>
              <div className="presets-grid">
                {presets.map((preset, index) => (
                  <button
                    key={index}
                    className="preset-card"
                    onClick={() => applyPreset(preset)}
                  >
                    <div className="preset-name">{preset.name}</div>
                    <div className="preset-details">
                      {preset.length} chars • {preset.includeSymbols ? 'Symbols' : 'No Symbols'}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Options Section */}
          <div className="options-section">
            <h3>Password Options</h3>
            
            {/* Length Slider */}
            <div className="option-group">
              <label className="option-label">
                Password Length: <span className="length-value">{options.length}</span>
              </label>
              <input
                type="range"
                min="4"
                max="50"
                value={options.length}
                onChange={(e) => updateOption('length', parseInt(e.target.value))}
                className="length-slider"
              />
              <div className="slider-labels">
                <span>4</span>
                <span>50</span>
              </div>
            </div>

            {/* Character Options */}
            <div className="option-group">
              <label className="option-label">Include Characters</label>
              <div className="checkboxes">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={options.includeUppercase}
                    onChange={(e) => updateOption('includeUppercase', e.target.checked)}
                  />
                  <span>Uppercase (A-Z)</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={options.includeLowercase}
                    onChange={(e) => updateOption('includeLowercase', e.target.checked)}
                  />
                  <span>Lowercase (a-z)</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={options.includeNumbers}
                    onChange={(e) => updateOption('includeNumbers', e.target.checked)}
                  />
                  <span>Numbers (0-9)</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={options.includeSymbols}
                    onChange={(e) => updateOption('includeSymbols', e.target.checked)}
                  />
                  <span>Symbols (!@#$%^&*)</span>
                </label>
              </div>
            </div>

            {/* Advanced Options */}
            <div className="option-group">
              <button 
                className="advanced-toggle"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                {showAdvanced ? '▼' : '▶'} Advanced Options
              </button>
              
              {showAdvanced && (
                <div className="advanced-options">
                  <div className="checkboxes">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={options.excludeSimilar}
                        onChange={(e) => updateOption('excludeSimilar', e.target.checked)}
                      />
                      <span>Exclude Similar (i, l, 1, L, o, 0, O)</span>
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={options.excludeAmbiguous}
                        onChange={(e) => updateOption('excludeAmbiguous', e.target.checked)}
                      />
                                             <span>Exclude Ambiguous ({`{ } [ ] ( ) / \\ ' " \` ~ , ; . < >`})</span>
                    </label>
                  </div>
                  
                  <div className="custom-chars">
                    <label className="option-label">Custom Characters to Include</label>
                    <input
                      type="text"
                      value={customCharacters}
                      onChange={(e) => setCustomCharacters(e.target.value)}
                      placeholder="Add custom characters"
                      className="custom-input"
                    />
                  </div>
                  
                  <div className="custom-chars">
                    <label className="option-label">Characters to Exclude</label>
                    <input
                      type="text"
                      value={excludeCharacters}
                      onChange={(e) => setExcludeCharacters(e.target.value)}
                      placeholder="Characters to exclude"
                      className="custom-input"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* History Section */}
          <div className="history-section">
            <h3>Password History</h3>
            {history.length === 0 ? (
              <p className="no-history">No passwords generated yet</p>
            ) : (
              <>
                <div className="history-list">
                  {history.map((entry, index) => (
                    <div key={index} className="history-item">
                      <div className="history-password">
                        <span className="password-text">{entry.password}</span>
                        <button 
                          className="copy-history-btn"
                          onClick={() => navigator.clipboard?.writeText(entry.password)}
                          title="Copy"
                        >
                          📋
                        </button>
                      </div>
                      <div className="history-meta">
                        <span className="history-strength" style={{ color: entry.strength === 'Strong' ? '#137333' : entry.strength === 'Good' ? '#34a853' : entry.strength === 'Fair' ? '#f9ab00' : '#ea4335' }}>
                          {entry.strength}
                        </span>
                        <span className="history-time">{entry.timestamp}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <button className="clear-history" onClick={clearHistory}>
                  Clear History
                </button>
              </>
            )}
          </div>
        </div>

        {/* Bottom Ad Banner */}
        <AdBanner adSlot="8989183638" style={{ marginTop: '40px' }} />
      </div>
    </div>
  );
}

export default PasswordGenerator; 